import React, { useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { format } from 'date-fns';
import SignatureCanvas from 'react-signature-canvas';
import { X } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_COLORS = {
  text: '#EF4444',
  signature: '#223368',
  initials: '#EC4899',
  date: '#F59E0B',
  checkbox: '#10B981',
  multiple: '#A855F7',
  radio: '#06B6D4',
};

export default function FormPreview({ template, onClose }) {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth] = useState(Math.min(window.innerWidth - 80, 700));
  const [fieldValues, setFieldValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const sigRefs = useRef({});
  const placements = template?.field_placements || [];

  const mockAppointmentDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  const updateField = (id, value) => setFieldValues(prev => ({ ...prev, [id]: value }));

  const toggleOption = (field, optionId, checked) => {
    setFieldValues(prev => {
      if (field.type === 'radio') {
        return { ...prev, [field.id]: checked ? optionId : null };
      }
      const cur = Array.isArray(prev[field.id]) ? prev[field.id] : [];
      const next = checked ? Array.from(new Set([...cur, optionId])) : cur.filter(o => o !== optionId);
      return { ...prev, [field.id]: next };
    });
  };

  const isOptionSelected = (field, optionId) => {
    const v = fieldValues[field.id];
    if (field.type === 'radio') return v === optionId;
    if (field.type === 'multiple') return Array.isArray(v) && v.includes(optionId);
    return false;
  };

  const answerDisplay = (f) => {
    const v = fieldValues[f.id];
    if (f.type === 'checkbox') return v ? 'Yes' : 'No';
    if (f.type === 'radio') {
      const opt = (f.options || []).find(o => o.id === v);
      return opt?.label || '(none)';
    }
    if (f.type === 'multiple') {
      const picked = (f.options || []).filter(o => Array.isArray(v) && v.includes(o.id)).map(o => o.label);
      return picked.length ? picked.join(', ') : '(none)';
    }
    if (f.type === 'date' && f.auto_fill === 'appointment_date') return format(mockAppointmentDate, 'MM/dd/yyyy');
    return v || '(empty)';
  };

  const handleSubmit = () => setSubmitted(true);
  const handleReset = () => {
    setFieldValues({});
    setSubmitted(false);
    Object.values(sigRefs.current).forEach(ref => ref?.clear?.());
  };

  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-[860px] max-h-[92vh] bg-[#1E293B] rounded-xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header — matches the patient-facing signing page chrome */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 border-b border-white/5">
          <div>
            <h3 className="text-[15px] font-bold text-white">{template.name}</h3>
            <p className="text-[11px] text-white/60">Preview — exactly what the patient will see</p>
          </div>
          <div className="flex items-center gap-2">
            {submitted && (
              <button onClick={handleReset} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-white/10 hover:bg-white/15 transition-all">
                Reset & Try Again
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 text-white/70 flex items-center justify-center hover:bg-white/15">
              <X size={14} strokeWidth={2.2} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {submitted ? (
            <div className="max-w-[500px] mx-auto text-center py-12">
              <div className="w-[52px] h-[52px] bg-[#D1FAE5] rounded-[14px] flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h2 className="text-[20px] font-bold text-[#111] mb-2">Form Submitted (Preview)</h2>
              <p className="text-[13px] text-[#666] mb-4">This is what patients see after signing. In production, the form is saved with a tamper-proof hash and audit trail.</p>
              <div className="bg-white rounded-xl p-4 text-left space-y-2 shadow-sm">
                <div className="text-[11px] font-bold text-[#999] uppercase tracking-wider mb-2">Submitted Values</div>
                {placements.filter(f => f.type !== 'signature' && f.type !== 'initials').map(f => (
                  <div key={f.id} className="flex justify-between gap-3 text-[12px] py-1 border-b border-[#f3f4f6] last:border-0">
                    <span className="text-[#666] flex-shrink-0">{f.label}</span>
                    <span className="text-[#111] font-medium text-right">{answerDisplay(f)}</span>
                  </div>
                ))}
                {placements.some(f => f.type === 'signature' || f.type === 'initials') && (
                  <div className="text-[11px] text-[#10B981] font-medium pt-1">Signature captured</div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Appointment date banner */}
              <div className="bg-white rounded-lg p-3 mb-3 shadow-sm text-center max-w-[700px] mx-auto">
                <span className="text-[11px] text-[#666]">Appointment Date: </span>
                <span className="text-[13px] font-bold text-[#111]">{format(mockAppointmentDate, 'EEEE, MMMM d, yyyy')}</span>
              </div>

              {/* PDF with interactive fields */}
              {template.document_url ? (
                <Document file={template.document_url} onLoadSuccess={({ numPages: n }) => setNumPages(n)}>
                  {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
                    <div key={pageNum} className="relative mb-4 shadow-lg rounded-lg overflow-hidden bg-white mx-auto" style={{ maxWidth: pageWidth }}>
                      <Page pageNumber={pageNum} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />

                      {placements.map(field => {
                        // Group field — render each option's own hit-area
                        if (field.type === 'multiple' || field.type === 'radio') {
                          return (field.options || [])
                            .filter(o => o.page === pageNum)
                            .map(opt => {
                              const selected = isOptionSelected(field, opt.id);
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => toggleOption(field, opt.id, !selected)}
                                  className="absolute cursor-pointer bg-transparent p-0"
                                  style={{
                                    left: `${opt.x}%`, top: `${opt.y}%`,
                                    width: `${opt.width}%`, height: `${opt.height}%`,
                                    border: selected
                                      ? '1.5px solid rgba(16,185,129,0.85)'
                                      : '1px solid rgba(107, 114, 128, 0.7)',
                                    background: selected ? 'rgba(16,185,129,0.10)' : 'transparent',
                                    borderRadius: 1,
                                  }}
                                >
                                  {selected && (
                                    <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" stroke="#111827" strokeWidth="4" strokeLinecap="round">
                                      <path d="M5 5 L19 19 M19 5 L5 19" />
                                    </svg>
                                  )}
                                </button>
                              );
                            });
                        }

                        // Non-group field — skip if not on this page
                        if (field.page !== pageNum) return null;
                        const color = FIELD_COLORS[field.type] || '#6B7280';
                        const isCheckbox = field.type === 'checkbox';
                        return (
                          <div
                            key={field.id}
                            className="absolute"
                            style={{
                              left: `${field.x}%`,
                              top: `${field.y}%`,
                              width: `${field.width}%`,
                              height: `${field.height}%`,
                              ...(isCheckbox ? {} : { minWidth: 50, minHeight: 20 }),
                            }}
                          >
                            {(field.type === 'signature' || field.type === 'initials') ? (
                              <div className="w-full h-full border-2 rounded bg-white/95 overflow-hidden" style={{ borderColor: color }}>
                                <SignatureCanvas
                                  ref={r => { sigRefs.current[field.id] = r; }}
                                  penColor="#111827"
                                  canvasProps={{ style: { width: '100%', height: '100%' } }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 text-center text-[8px] font-medium py-0.5 bg-white/90" style={{ color }}>
                                  {field.type === 'signature' ? 'Sign here' : 'Initials'}
                                  <button
                                    onClick={() => sigRefs.current[field.id]?.clear()}
                                    className="ml-2 underline opacity-60 hover:opacity-100"
                                  >
                                    clear
                                  </button>
                                </div>
                              </div>
                            ) : field.type === 'checkbox' ? (
                              <button
                                type="button"
                                onClick={() => updateField(field.id, !fieldValues[field.id])}
                                className="w-full h-full cursor-pointer bg-transparent"
                                style={{
                                  border: '1px solid rgba(107, 114, 128, 0.7)',
                                  borderRadius: 1,
                                  padding: 0,
                                }}
                              >
                                {fieldValues[field.id] && (
                                  <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" stroke="#111827" strokeWidth="4" strokeLinecap="round">
                                    <path d="M5 5 L19 19 M19 5 L5 19" />
                                  </svg>
                                )}
                              </button>
                            ) : field.type === 'date' && field.auto_fill === 'appointment_date' ? (
                              <input
                                type="text"
                                readOnly
                                value={format(mockAppointmentDate, 'MM/dd/yyyy')}
                                className="w-full h-full px-1 text-[11px] text-[#111] font-medium bg-transparent focus:outline-none"
                                style={{ border: '1px dashed rgba(156, 163, 175, 0.45)', borderRadius: 2 }}
                              />
                            ) : (
                              <input
                                type={field.type === 'date' ? 'date' : 'text'}
                                value={fieldValues[field.id] || ''}
                                onChange={e => updateField(field.id, e.target.value)}
                                placeholder={field.label}
                                className="w-full h-full px-1 text-[11px] text-[#111] font-medium bg-transparent focus:outline-none focus:bg-[#FFF9C4] placeholder:text-[#9CA3AF] placeholder:italic"
                                style={{ border: '1px dashed rgba(156, 163, 175, 0.45)', borderRadius: 2 }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </Document>
              ) : (
                <div className="text-center py-16 text-[14px] text-[#999]">No document uploaded</div>
              )}

              {/* Submit */}
              <div className="bg-white rounded-lg p-4 shadow-sm max-w-[700px] mx-auto mt-2">
                <p className="text-[11px] text-[#666] mb-3 leading-relaxed">
                  By signing this document, you acknowledge that all information is accurate.
                  The appointment date is {format(mockAppointmentDate, 'MMMM d, yyyy')}, regardless of when this document is signed.
                </p>
                <button
                  onClick={handleSubmit}
                  className="w-full py-3 rounded-[10px] text-[14px] font-semibold text-white bg-[#0DA7CA] hover:bg-[#0891B2] transition-all shadow-lg"
                >
                  Sign & Submit (Preview)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
